/**
 * gfxId → 몬스터 계열(MobFamily) 표 — **자동 생성됨. 직접 고치지 말 것.**
 *
 * regenerate: python3 scripts/gen_mob_families.py
 *
 * 서버의 몹 스폰 패킷에는 gfxId와 이름만 있고 생김새 정보가 없다. 원본
 * 클라이언트 리소스가 없는 웹 클라이언트는 DB(`npcs` 테이블)의 이름을
 * 키워드로 훑어 12개 계열로 접어 넣고, 계열별 SVG를 대신 그린다.
 *
 * 키워드로 아무것도 못 맞춘 gfxId는 **일부러 표에 넣지 않는다** — 런타임에서
 * inferMobFamily()가 'humanoid'로 폴백하므로 표를 부풀릴 이유가 없다.
 */
import type { MobFamily } from '../utils/mobSprites';

/**
 * 이름 키워드 → 계열 규칙. **위에서부터 처음 걸린 규칙이 이긴다.**
 *
 * 이 표는 아래 gfx 표를 만들 때 쓴 것과 **같은 규칙**이다. DB에 없는 gfxId가
 * 와도 런타임에서 이름으로 같은 판정을 내릴 수 있도록 함께 내보낸다.
 * 키워드는 이미 정규화(공백 제거 + 소문자)된 상태다.
 */
export const MOB_FAMILY_KEYWORDS: ReadonlyArray<readonly [string, MobFamily]> = [
  ['드래곤플라이', 'insect'],
  ['젤라틴큐브', 'slime'],
  ['드레이크의영혼', 'undead'],
  ['드레이크의유령', 'undead'],
  ['용의계란', 'object'],
  ['개미의알', 'object'],
  ['얼음여왕', 'humanoid'],
  ['얼음의여왕', 'humanoid'],
  ['나이트메어', 'beast'],
  ['불꽃의캥거루', 'beast'],
  ['가오리', 'aquatic'],
  ['시호스', 'aquatic'],
  ['아이스데몬', 'humanoid'],
  ['테베갑충석', 'insect'],
  ['종이우산귀신', 'undead'],
  ['살생석', 'object'],
  ['treasurechest', 'object'],
  ['magicbox', 'object'],
  ['chest', 'object'],
  ['box', 'object'],
  ['wall', 'object'],
  ['fountain', 'object'],
  ['rune', 'object'],
  ['scarecrow', 'object'],
  ['상자', 'object'],
  ['미믹', 'object'],
  ['보물', 'object'],
  ['輝宝', 'object'],
  ['분수', 'object'],
  ['게시판', 'object'],
  ['간판', 'object'],
  ['수호탑', 'object'],
  ['제단', 'object'],
  ['오벨리스크', 'object'],
  ['허수아비', 'object'],
  ['포탄', 'object'],
  ['폭탄', 'object'],
  ['깃발', 'object'],
  ['입구', 'object'],
  ['도구류', 'object'],
  ['무기부대', 'object'],
  ['큐브', 'object'],
  ['댄싱소드', 'object'],
  ['균열', 'object'],
  ['진주', 'object'],
  ['에그', 'object'],
  ['비석', 'object'],
  ['석상', 'object'],
  ['버섯', 'object'],
  ['펑거스', 'object'],
  ['플라워', 'object'],
  ['만드라고라', 'object'],
  ['엔트', 'object'],
  ['폭탄꽃', 'object'],
  ['フラワー', 'object'],
  ['빠른샌드', 'object'],
  ['クイックサンド', 'object'],
  ['드래곤', 'dragon'],
  ['ドラゴン', 'dragon'],
  ['드래고니안', 'dragon'],
  ['드레이크', 'dragon'],
  ['해츨링', 'dragon'],
  ['ハッチリング', 'dragon'],
  ['와이번', 'dragon'],
  ['와이반', 'dragon'],
  ['안타라스', 'dragon'],
  ['발라카스', 'dragon'],
  ['파푸리온', 'dragon'],
  ['린드비오르', 'dragon'],
  ['린드비오르', 'dragon'],
  ['좀비', 'undead'],
  ['ゾンビ', 'undead'],
  ['해골', 'undead'],
  ['스켈레톤', 'undead'],
  ['スケルトン', 'undead'],
  ['촉루', 'undead'],
  ['구울', 'undead'],
  ['ゴースト', 'undead'],
  ['유령', 'undead'],
  ['망령', 'undead'],
  ['혼령', 'undead'],
  ['악령', 'undead'],
  ['영혼', 'undead'],
  ['리치', 'undead'],
  ['뱀파이어', 'undead'],
  ['머미', 'undead'],
  ['미라', 'undead'],
  ['언데드', 'undead'],
  ['가스트', 'undead'],
  ['네크로맨서', 'undead'],
  ['강령술사', 'undead'],
  ['리퍼', 'undead'],
  ['팬텀', 'undead'],
  ['데스', 'undead'],
  ['죽음', 'undead'],
  ['귀신', 'undead'],
  ['ろくろ首', 'undead'],
  ['스피리드', 'undead'],
  ['스파토이', 'undead'],
  ['시체', 'undead'],
  ['무덤', 'undead'],
  ['라버본', 'undead'],
  ['뼈', 'undead'],
  ['악몽', 'undead'],
  ['익사한', 'undead'],
  ['잭오', 'undead'],
  ['랜턴', 'undead'],
  ['썩어가는', 'undead'],
  ['부식된', 'undead'],
  ['부패한', 'undead'],
  ['골렘', 'golem'],
  ['ゴーレム', 'golem'],
  ['리빙아머', 'golem'],
  ['가디언아머', 'golem'],
  ['아머', 'golem'],
  ['아수라상', 'golem'],
  ['눈사람', 'golem'],
  ['스노우맨', 'golem'],
  ['정령', 'elemental'],
  ['엘리멘탈', 'elemental'],
  ['エレメンタル', 'elemental'],
  ['이프리트', 'elemental'],
  ['사라만다', 'elemental'],
  ['살라만더', 'elemental'],
  ['라이트닝', 'elemental'],
  ['화염', 'elemental'],
  ['불의', 'elemental'],
  ['물의', 'elemental'],
  ['바람의', 'elemental'],
  ['땅의', 'elemental'],
  ['흙의', 'elemental'],
  ['대지의', 'elemental'],
  ['빙하의', 'elemental'],
  ['이빨', 'elemental'],
  ['시버', 'elemental'],
  ['샤스키', 'elemental'],
  ['암운', 'elemental'],
  ['구름', 'elemental'],
  ['화령', 'elemental'],
  ['수령', 'elemental'],
  ['지령', 'elemental'],
  ['풍령', 'elemental'],
  ['아이스', 'elemental'],
  ['アイス', 'elemental'],
  ['얼음', 'elemental'],
  ['炎', 'elemental'],
  ['火', 'elemental'],
  ['熱', 'elemental'],
  ['紅蓮', 'elemental'],
  ['風', 'elemental'],
  ['雷', 'elemental'],
  ['슬라임', 'slime'],
  ['スライム', 'slime'],
  ['브롭', 'slime'],
  ['하피', 'flying'],
  ['그리폰', 'flying'],
  ['グリフォン', 'flying'],
  ['박쥐', 'flying'],
  ['뱃', 'flying'],
  ['피닉스', 'flying'],
  ['가고일', 'flying'],
  ['ガーゴイル', 'flying'],
  ['pigeon', 'flying'],
  ['비둘기', 'flying'],
  ['오리', 'flying'],
  ['패롯', 'flying'],
  ['앵무', 'flying'],
  ['가루다', 'flying'],
  ['키위', 'flying'],
  ['제비', 'flying'],
  ['비홀더', 'flying'],
  ['괴물눈', 'flying'],
  ['플로팅', 'flying'],
  ['フローティングアイ', 'flying'],
  ['스핑크스', 'flying'],
  ['호루스', 'flying'],
  ['페어리', 'flying'],
  ['크로울링크로', 'flying'],
  ['상어', 'aquatic'],
  ['인어', 'aquatic'],
  ['머맨', 'aquatic'],
  ['반어인', 'aquatic'],
  ['크랩', 'aquatic'],
  ['크러스트시안', 'aquatic'],
  ['크라켄', 'aquatic'],
  ['해파리', 'aquatic'],
  ['오징어', 'aquatic'],
  ['실라칸스', 'aquatic'],
  ['심해어', 'aquatic'],
  ['조개', 'aquatic'],
  ['시댄서', 'aquatic'],
  ['물고기', 'aquatic'],
  ['말미잘', 'aquatic'],
  ['뱀장어', 'aquatic'],
  ['시헌터', 'aquatic'],
  ['펭귄', 'aquatic'],
  ['일렉카둠', 'aquatic'],
  ['갓파', 'aquatic'],
  ['fish', 'aquatic'],
  ['리자드', 'reptile'],
  ['リザード', 'reptile'],
  ['도마뱀', 'reptile'],
  ['도롱뇽', 'reptile'],
  ['뱀', 'reptile'],
  ['스네이크', 'reptile'],
  ['바실리스크', 'reptile'],
  ['バシリスク', 'reptile'],
  ['코카트리스', 'reptile'],
  ['악어', 'reptile'],
  ['크로커다일', 'reptile'],
  ['크로코다일', 'reptile'],
  ['クロコダイル', 'reptile'],
  ['거북', 'reptile'],
  ['터틀', 'reptile'],
  ['히드라', 'reptile'],
  ['메두사', 'reptile'],
  ['라미아', 'reptile'],
  ['토드', 'reptile'],
  ['개구리', 'reptile'],
  ['튜폰', 'reptile'],
  ['개미', 'insect'],
  ['アント', 'insect'],
  ['앤트', 'insect'],
  ['전갈', 'insect'],
  ['스콜피온', 'insect'],
  ['スコーピオン', 'insect'],
  ['거미', 'insect'],
  ['셀로브', 'insect'],
  ['셸로브', 'insect'],
  ['스파이더', 'insect'],
  ['아라크네', 'insect'],
  ['벌레', 'insect'],
  ['웜', 'insect'],
  ['ワーム', 'insect'],
  ['센티피드', 'insect'],
  ['지네', 'insect'],
  ['킬러비', 'insect'],
  ['나비', 'insect'],
  ['butterfly', 'insect'],
  ['갑충', 'insect'],
  ['웅골리언트', 'insect'],
  ['옹골리언트', 'insect'],
  ['늑대', 'beast'],
  ['울프', 'beast'],
  ['ウルフ', 'beast'],
  ['베어', 'beast'],
  ['ベア', 'beast'],
  ['곰', 'beast'],
  ['타이거', 'beast'],
  ['タイガー', 'beast'],
  ['티거', 'beast'],
  ['호랑', 'beast'],
  ['쥐', 'beast'],
  ['랫', 'beast'],
  ['ラット', 'beast'],
  ['고양이', 'beast'],
  ['캣', 'beast'],
  ['여우', 'beast'],
  ['폭스', 'beast'],
  ['토끼', 'beast'],
  ['래빗', 'beast'],
  ['사슴', 'beast'],
  ['멧돼지', 'beast'],
  ['판다', 'beast'],
  ['쿠거', 'beast'],
  ['라쿤', 'beast'],
  ['원숭이', 'beast'],
  ['멍키', 'beast'],
  ['호스', 'beast'],
  ['유니콘', 'beast'],
  ['켈베로스', 'beast'],
  ['도베르만', 'beast'],
  ['비글', 'beast'],
  ['콜리', 'beast'],
  ['허스키', 'beast'],
  ['세퍼드', 'beast'],
  ['세인트버나드', 'beast'],
  ['진돗개', 'beast'],
  ['젖소', 'beast'],
  ['캥거루', 'beast'],
  ['두더지', 'beast'],
  ['라이칸', 'beast'],
  ['미노타우르스', 'beast'],
  ['마이노', 'beast'],
  ['짐승', 'beast'],
  ['바운드', 'beast'],
  ['바쿠쿠', 'beast'],
  ['이타치', 'beast'],
  ['구미호', 'beast'],
  ['玉藻', 'beast'],
  ['설인', 'beast'],
  ['イエティ', 'beast'],
  ['비스트', 'beast'],
  ['견', 'beast'],
  ['키메라', 'beast'],
  ['우귀', 'beast'],
  ['와일드팡', 'beast'],
  ['풋마요', 'beast'],
  ['フット', 'beast'],
  ['오크', 'humanoid'],
  ['オーク', 'humanoid'],
  ['엘프', 'humanoid'],
  ['エルフ', 'humanoid'],
  ['인간', 'humanoid'],
  ['드워프', 'humanoid'],
  ['ドワーフ', 'humanoid'],
  ['난쟁이', 'humanoid'],
  ['고블린', 'humanoid'],
  ['ゴブリン', 'humanoid'],
  ['트롤', 'humanoid'],
  ['오우거', 'humanoid'],
  ['자이언트', 'humanoid'],
  ['ジャイアント', 'humanoid'],
  ['임프', 'humanoid'],
  ['데몬', 'humanoid'],
  ['서큐버스', 'humanoid'],
  ['발록', 'humanoid'],
  ['바포메트', 'humanoid'],
  ['베레스', 'humanoid'],
  ['악마', 'humanoid'],
  ['기사', 'humanoid'],
  ['나이트', 'humanoid'],
  ['병사', 'humanoid'],
  ['궁수', 'humanoid'],
  ['마법사', 'humanoid'],
  ['위저드', 'humanoid'],
  ['위자드', 'humanoid'],
  ['전사', 'humanoid'],
  ['투사', 'humanoid'],
  ['장군', 'humanoid'],
  ['제너럴', 'humanoid'],
  ['파이터', 'humanoid'],
  ['아처', 'humanoid'],
  ['아쳐', 'humanoid'],
  ['소드맨', 'humanoid'],
  ['창병', 'humanoid'],
  ['도끼', 'humanoid'],
  ['샤먼', 'humanoid'],
  ['주술사', 'humanoid'],
  ['산적', 'humanoid'],
  ['도적', 'humanoid'],
  ['시프', 'humanoid'],
  ['어쌔신', 'humanoid'],
  ['레인저', 'humanoid'],
  ['사이클롭스', 'humanoid'],
  ['그렘린', 'humanoid'],
  ['오움', 'humanoid'],
  ['에틴', 'humanoid'],
  ['에티', 'humanoid'],
  ['엘모어', 'humanoid'],
  ['텐구', 'humanoid'],
  ['혈거', 'humanoid'],
  ['코볼트', 'humanoid'],
  ['근위', 'humanoid'],
  ['친위', 'humanoid'],
  ['수호자', 'humanoid'],
  ['대법관', 'humanoid'],
  ['단장', 'humanoid'],
  ['군왕', 'humanoid'],
  ['제사장', 'humanoid'],
  ['사제', 'humanoid'],
  ['신도', 'humanoid'],
  ['성도', 'humanoid'],
  ['인형', 'humanoid'],
  ['스카우트', 'humanoid'],
  ['밀사', 'humanoid'],
  ['조사원', 'humanoid'],
  ['간수', 'humanoid'],
  ['죄인', 'humanoid'],
  ['순찰병', 'humanoid'],
  ['보초병', 'humanoid'],
  ['장로', 'humanoid'],
  ['엘더', 'humanoid'],
  ['메이지', 'humanoid'],
  ['매지스터', 'humanoid'],
  ['워리어', 'humanoid'],
  ['가디언', 'humanoid'],
  ['센티넬', 'humanoid'],
  ['마스터', 'humanoid'],
  ['사냥꾼', 'humanoid'],
  ['닌자', 'humanoid'],
  ['사무라이', 'humanoid'],
  ['다이묘', 'humanoid'],
  ['음양사', 'humanoid'],
  ['무사', 'humanoid'],
  ['무녀', 'humanoid'],
  ['도펠', 'humanoid'],
  ['도플', 'humanoid'],
  ['로드', 'humanoid'],
  ['군주', 'humanoid'],
  ['퀸', 'humanoid'],
  ['백작', 'humanoid'],
  ['암살', 'humanoid'],
  ['경비병', 'humanoid'],
  ['관리인', 'humanoid'],
  ['군인', 'humanoid'],
  ['숲지기', 'humanoid'],
  ['조련사', 'humanoid'],
  ['복수', 'humanoid'],
  ['신명', 'humanoid'],
  ['도깨비', 'humanoid'],
  ['아누비스', 'humanoid'],
  ['페이퍼맨', 'humanoid'],
  ['크리처', 'humanoid'],
  ['크리쳐', 'humanoid'],
  ['시어', 'humanoid'],
  ['타락', 'humanoid'],
  ['혼돈', 'humanoid'],
  ['포레스터', 'humanoid'],
  ['셔먼', 'humanoid'],
  ['エルモア', 'humanoid'],
  ['ソルジャー', 'humanoid'],
  ['ウィザード', 'humanoid'],
  ['ジェネラル', 'humanoid'],
];

/** gfxId → 계열. 미분류 gfxId는 들어 있지 않다. */
export const MOB_FAMILY_BY_GFX: Readonly<Record<number, MobFamily>> = {
  0: 'humanoid',
  29: 'flying',
  30: 'undead',
  31: 'humanoid',
  32: 'humanoid',
  49: 'golem',
  52: 'undead',
  53: 'humanoid',
  54: 'humanoid',
  55: 'reptile',
  56: 'humanoid',
  85: 'object',
  86: 'object',
  87: 'object',
  95: 'insect',
  96: 'beast',
  98: 'humanoid',
  114: 'object',
  116: 'object',
  118: 'humanoid',
  120: 'object',
  134: 'object',
  136: 'object',
  144: 'undead',
  145: 'undead',
  146: 'insect',
  148: 'humanoid',
  152: 'beast',
  155: 'elemental',
  168: 'object',
  173: 'undead',
  176: 'object',
  183: 'humanoid',
  185: 'undead',
  187: 'undead',
  240: 'undead',
  246: 'flying',
  255: 'undead',
  257: 'undead',
  264: 'object',
  335: 'humanoid',
  361: 'object',
  702: 'humanoid',
  764: 'object',
  779: 'object',
  782: 'humanoid',
  784: 'humanoid',
  786: 'humanoid',
  788: 'humanoid',
  790: 'humanoid',
  792: 'humanoid',
  800: 'humanoid',
  824: 'humanoid',
  841: 'object',
  843: 'object',
  847: 'object',
  851: 'object',
  852: 'object',
  854: 'flying',
  864: 'insect',
  865: 'insect',
  868: 'object',
  874: 'object',
  894: 'beast',
  896: 'flying',
  929: 'beast',
  931: 'beast',
  934: 'beast',
  936: 'beast',
  938: 'beast',
  945: 'beast',
  947: 'beast',
  951: 'beast',
  971: 'humanoid',
  979: 'beast',
  992: 'humanoid',
  998: 'beast',
  999: 'beast',
  1002: 'beast',
  1003: 'beast',
  1011: 'humanoid',
  1014: 'flying',
  1020: 'humanoid',
  1022: 'humanoid',
  1024: 'flying',
  1036: 'object',
  1037: 'insect',
  1039: 'insect',
  1041: 'reptile',
  1047: 'insect',
  1052: 'reptile',
  1055: 'object',
  1059: 'humanoid',
  1082: 'humanoid',
  1093: 'object',
  1094: 'flying',
  1096: 'undead',
  1098: 'humanoid',
  1104: 'undead',
  1106: 'undead',
  1108: 'beast',
  1110: 'beast',
  1112: 'humanoid',
  1116: 'humanoid',
  1121: 'undead',
  1123: 'flying',
  1125: 'humanoid',
  1128: 'humanoid',
  1130: 'undead',
  1132: 'undead',
  1134: 'undead',
  1152: 'humanoid',
  1154: 'humanoid',
  1158: 'humanoid',
  1162: 'beast',
  1171: 'object',
  1172: 'object',
  1173: 'reptile',
  1180: 'humanoid',
  1184: 'humanoid',
  1202: 'humanoid',
  1204: 'flying',
  1206: 'humanoid',
  1211: 'humanoid',
  1222: 'humanoid',
  1236: 'beast',
  1245: 'dragon',
  1250: 'humanoid',
  1254: 'humanoid',
  1256: 'beast',
  1276: 'undead',
  1284: 'object',
  1307: 'humanoid',
  1314: 'humanoid',
  1318: 'undead',
  1321: 'undead',
  1390: 'insect',
  1449: 'object',
  1450: 'object',
  1479: 'object',
  1515: 'humanoid',
  1517: 'beast',
  1522: 'object',
  1525: 'golem',
  1533: 'undead',
  1538: 'humanoid',
  1540: 'beast',
  1542: 'humanoid',
  1544: 'object',
  1546: 'object',
  1555: 'humanoid',
  1557: 'object',
  1559: 'object',
  1569: 'humanoid',
  1571: 'humanoid',
  1572: 'reptile',
  1574: 'reptile',
  1576: 'undead',
  1578: 'golem',
  1580: 'aquatic',
  1584: 'aquatic',
  1586: 'aquatic',
  1590: 'dragon',
  1595: 'beast',
  1597: 'reptile',
  1600: 'reptile',
  1603: 'aquatic',
  1610: 'aquatic',
  1612: 'beast',
  1614: 'humanoid',
  1616: 'aquatic',
  1619: 'humanoid',
  1623: 'aquatic',
  1626: 'aquatic',
  1628: 'aquatic',
  1630: 'object',
  1632: 'aquatic',
  1634: 'aquatic',
  1636: 'aquatic',
  1638: 'aquatic',
  1640: 'aquatic',
  1642: 'beast',
  1644: 'aquatic',
  1648: 'object',
  1649: 'dragon',
  1653: 'aquatic',
  1655: 'aquatic',
  1657: 'aquatic',
  1659: 'humanoid',
  1666: 'elemental',
  1670: 'object',
  1672: 'object',
  1678: 'object',
  1681: 'object',
  1684: 'object',
  1702: 'object',
  1705: 'object',
  1707: 'object',
  1710: 'object',
  1719: 'object',
  1720: 'object',
  1721: 'object',
  1722: 'object',
  1723: 'object',
  1724: 'object',
  1725: 'object',
  1726: 'object',
  1727: 'object',
  1728: 'object',
  1758: 'object',
  1759: 'object',
  1762: 'golem',
  1770: 'humanoid',
  1773: 'elemental',
  1780: 'beast',
  1791: 'flying',
  1820: 'object',
  1828: 'object',
  1832: 'humanoid',
  1841: 'elemental',
  1844: 'insect',
  1995: 'object',
  2001: 'dragon',
  2064: 'golem',
  2083: 'object',
  2084: 'object',
  2086: 'beast',
  2090: 'humanoid',
  2112: 'humanoid',
  2118: 'humanoid',
  2134: 'humanoid',
  2137: 'humanoid',
  2143: 'humanoid',
  2145: 'beast',
  2147: 'golem',
  2150: 'humanoid',
  2158: 'elemental',
  2205: 'object',
  2207: 'object',
  2231: 'object',
  2240: 'humanoid',
  2266: 'elemental',
  2267: 'elemental',
  2268: 'elemental',
  2273: 'elemental',
  2274: 'elemental',
  2275: 'elemental',
  2276: 'elemental',
  2282: 'elemental',
  2299: 'object',
  2302: 'object',
  2308: 'flying',
  2332: 'beast',
  2335: 'humanoid',
  2337: 'humanoid',
  2340: 'humanoid',
  2350: 'reptile',
  2351: 'reptile',
  2352: 'reptile',
  2353: 'flying',
  2354: 'reptile',
  2355: 'reptile',
  2356: 'beast',
  2357: 'beast',
  2358: 'beast',
  2359: 'reptile',
  2360: 'reptile',
  2361: 'humanoid',
  2362: 'dragon',
  2363: 'flying',
  2364: 'beast',
  2365: 'beast',
  2366: 'beast',
  2367: 'beast',
  2368: 'beast',
  2369: 'insect',
  2370: 'insect',
  2371: 'humanoid',
  2372: 'undead',
  2373: 'undead',
  2379: 'dragon',
  2382: 'humanoid',
  2389: 'humanoid',
  2392: 'beast',
  2394: 'object',
  2400: 'humanoid',
  2402: 'beast',
  2407: 'beast',
  2409: 'dragon',
  2417: 'humanoid',
  2421: 'humanoid',
  2435: 'humanoid',
  2438: 'insect',
  2443: 'humanoid',
  2457: 'insect',
  2460: 'insect',
  2489: 'humanoid',
  2495: 'undead',
  2499: 'undead',
  2501: 'object',
  2503: 'object',
  2504: 'object',
  2510: 'object',
  2513: 'object',
  2516: 'humanoid',
  2524: 'humanoid',
  2531: 'humanoid',
  2533: 'humanoid',
  2535: 'reptile',
  2538: 'humanoid',
  2541: 'slime',
  2544: 'dragon',
  2665: 'object',
  2667: 'object',
  2669: 'object',
  2671: 'object',
  2674: 'object',
  2711: 'humanoid',
  2714: 'humanoid',
  2716: 'undead',
  2727: 'elemental',
  2738: 'elemental',
  2741: 'elemental',
  2747: 'elemental',
  2749: 'elemental',
  2752: 'undead',
  2755: 'beast',
  2757: 'undead',
  2763: 'humanoid',
  2771: 'golem',
  2773: 'humanoid',
  2788: 'insect',
  2801: 'humanoid',
  2803: 'undead',
  2807: 'humanoid',
  2820: 'undead',
  2822: 'object',
  2823: 'object',
  2824: 'object',
  2825: 'object',
  2826: 'object',
  2827: 'object',
  2828: 'object',
  2829: 'humanoid',
  2831: 'object',
  2832: 'object',
  2833: 'object',
  2834: 'object',
  2835: 'object',
  2836: 'object',
  2837: 'object',
  2839: 'object',
  2840: 'object',
  2841: 'object',
  2842: 'object',
  2843: 'humanoid',
  2846: 'object',
  2847: 'object',
  2848: 'object',
  2852: 'object',
  2853: 'object',
  2854: 'object',
  2855: 'object',
  2856: 'object',
  2857: 'object',
  2862: 'object',
  2863: 'object',
  2864: 'object',
  2865: 'object',
  2885: 'flying',
  2887: 'insect',
  2889: 'insect',
  2891: 'insect',
  2907: 'reptile',
  2966: 'humanoid',
  2972: 'undead',
  2973: 'undead',
  2974: 'undead',
  2975: 'undead',
  2976: 'humanoid',
  2977: 'humanoid',
  2978: 'humanoid',
  2992: 'object',
  3044: 'humanoid',
  3049: 'beast',
  3066: 'humanoid',
  3073: 'undead',
  3083: 'undead',
  3086: 'undead',
  3089: 'humanoid',
  3092: 'undead',
  3103: 'humanoid',
  3107: 'beast',
  3132: 'beast',
  3134: 'beast',
  3136: 'humanoid',
  3143: 'beast',
  3154: 'beast',
  3156: 'beast',
  3178: 'beast',
  3182: 'beast',
  3184: 'beast',
  3188: 'beast',
  3191: 'object',
  3195: 'humanoid',
  3199: 'beast',
  3201: 'object',
  3211: 'beast',
  3351: 'object',
  3357: 'beast',
  3360: 'elemental',
  3364: 'undead',
  3365: 'elemental',
  3366: 'humanoid',
  3376: 'humanoid',
  3380: 'undead',
  3387: 'undead',
  3391: 'undead',
  3402: 'humanoid',
  3404: 'humanoid',
  3409: 'beast',
  3412: 'humanoid',
  3415: 'flying',
  3418: 'undead',
  3448: 'humanoid',
  3449: 'humanoid',
  3450: 'humanoid',
  3478: 'beast',
  3520: 'object',
  3547: 'beast',
  3566: 'elemental',
  3568: 'object',
  3582: 'beast',
  3590: 'humanoid',
  3592: 'humanoid',
  3596: 'humanoid',
  3636: 'humanoid',
  3638: 'humanoid',
  3640: 'humanoid',
  3642: 'humanoid',
  3644: 'humanoid',
  3654: 'insect',
  3659: 'humanoid',
  3661: 'humanoid',
  3664: 'humanoid',
  3678: 'object',
  3694: 'humanoid',
  3698: 'humanoid',
  3744: 'humanoid',
  3747: 'humanoid',
  3783: 'beast',
  3800: 'humanoid',
  3801: 'humanoid',
  3860: 'humanoid',
  3864: 'humanoid',
  3867: 'humanoid',
  3890: 'humanoid',
  3891: 'humanoid',
  3892: 'humanoid',
  3893: 'humanoid',
  3894: 'humanoid',
  3895: 'humanoid',
  3896: 'humanoid',
  3897: 'humanoid',
  3898: 'humanoid',
  3899: 'humanoid',
  3900: 'humanoid',
  3912: 'humanoid',
  3914: 'golem',
  3916: 'beast',
  3918: 'beast',
  3920: 'beast',
  3922: 'beast',
  3926: 'humanoid',
  3928: 'undead',
  3930: 'undead',
  3957: 'humanoid',
  3969: 'humanoid',
  3984: 'humanoid',
  3989: 'humanoid',
  4003: 'humanoid',
  4005: 'undead',
  4009: 'elemental',
  4010: 'elemental',
  4011: 'elemental',
  4012: 'elemental',
  4013: 'elemental',
  4017: 'elemental',
  4024: 'elemental',
  4025: 'elemental',
  4028: 'elemental',
  4029: 'elemental',
  4030: 'elemental',
  4034: 'humanoid',
  4038: 'beast',
  4041: 'elemental',
  4043: 'humanoid',
  4045: 'humanoid',
  4047: 'elemental',
  4051: 'undead',
  4058: 'insect',
  4060: 'insect',
  4068: 'humanoid',
  4083: 'elemental',
  4088: 'beast',
  4091: 'humanoid',
  4095: 'undead',
  4097: 'reptile',
  4100: 'beast',
  4104: 'undead',
  4121: 'beast',
  4125: 'humanoid',
  4133: 'beast',
  4147: 'flying',
  4149: 'reptile',
  4151: 'reptile',
  4168: 'beast',
  4171: 'beast',
  4183: 'object',
  4185: 'object',
  4186: 'undead',
  4188: 'undead',
  4190: 'undead',
  4192: 'reptile',
  4207: 'beast',
  4209: 'undead',
  4212: 'undead',
  4215: 'undead',
  4218: 'undead',
  4260: 'humanoid',
  4261: 'humanoid',
  4262: 'humanoid',
  4359: 'elemental',
  4364: 'object',
  4366: 'object',
  4403: 'elemental',
  4404: 'elemental',
  4405: 'elemental',
  4407: 'elemental',
  4408: 'elemental',
  4410: 'elemental',
  4417: 'humanoid',
  4420: 'humanoid',
  4423: 'humanoid',
  4426: 'humanoid',
  4429: 'humanoid',
  4457: 'humanoid',
  4460: 'humanoid',
  4463: 'humanoid',
  4466: 'humanoid',
  4472: 'humanoid',
  4475: 'humanoid',
  4493: 'undead',
  4502: 'undead',
  4516: 'humanoid',
  4517: 'humanoid',
  4519: 'humanoid',
  4521: 'humanoid',
  4527: 'humanoid',
  4531: 'humanoid',
  4533: 'humanoid',
  4542: 'beast',
  4544: 'humanoid',
  4550: 'humanoid',
  4559: 'humanoid',
  4561: 'humanoid',
  4563: 'humanoid',
  4574: 'humanoid',
  4576: 'humanoid',
  4582: 'beast',
  4587: 'undead',
  4595: 'humanoid',
  4603: 'humanoid',
  4609: 'humanoid',
  4615: 'humanoid',
  4618: 'humanoid',
  4622: 'humanoid',
  4625: 'humanoid',
  4632: 'elemental',
  4633: 'elemental',
  4634: 'elemental',
  4643: 'beast',
  4649: 'dragon',
  4652: 'humanoid',
  4657: 'humanoid',
  4664: 'undead',
  4667: 'humanoid',
  4670: 'undead',
  4684: 'beast',
  4689: 'humanoid',
  4691: 'undead',
  4693: 'humanoid',
  4701: 'humanoid',
  4708: 'reptile',
  4738: 'aquatic',
  4740: 'beast',
  4744: 'humanoid',
  4753: 'humanoid',
  4771: 'reptile',
  4786: 'elemental',
  4787: 'elemental',
  4788: 'aquatic',
  4796: 'undead',
  4820: 'beast',
  4822: 'beast',
  4851: 'humanoid',
  4854: 'humanoid',
  4855: 'humanoid',
  4867: 'golem',
  4910: 'golem',
  4912: 'golem',
  4915: 'elemental',
  4930: 'humanoid',
  4935: 'undead',
  4937: 'undead',
  4942: 'beast',
  5007: 'reptile',
  5014: 'undead',
  5033: 'undead',
  5065: 'beast',
  5072: 'beast',
  5089: 'beast',
  5107: 'golem',
  5110: 'humanoid',
  5112: 'humanoid',
  5127: 'undead',
  5212: 'humanoid',
  5259: 'reptile',
  5265: 'elemental',
  5269: 'elemental',
  5274: 'humanoid',
  5282: 'dragon',
  5284: 'reptile',
  5289: 'aquatic',
  5293: 'humanoid',
  5297: 'humanoid',
  5317: 'elemental',
  5324: 'elemental',
  5331: 'elemental',
  5338: 'elemental',
  5348: 'golem',
  5365: 'humanoid',
  5373: 'humanoid',
  5405: 'undead',
  5412: 'undead',
  5447: 'humanoid',
  5471: 'humanoid',
  5478: 'humanoid',
  5484: 'undead',
  5507: 'humanoid',
  5511: 'humanoid',
  5516: 'humanoid',
  5547: 'aquatic',
  5554: 'aquatic',
  5567: 'humanoid',
  5574: 'object',
  5596: 'humanoid',
  5645: 'undead',
  5677: 'undead',
  5683: 'humanoid',
  5684: 'flying',
  5685: 'object',
  5687: 'humanoid',
  5688: 'humanoid',
  5689: 'humanoid',
  5690: 'humanoid',
  5691: 'humanoid',
  5693: 'humanoid',
  5694: 'humanoid',
  5695: 'humanoid',
  5708: 'aquatic',
  5723: 'humanoid',
  5724: 'humanoid',
  5739: 'humanoid',
  5742: 'humanoid',
  5745: 'humanoid',
  5751: 'humanoid',
  5754: 'humanoid',
  5760: 'humanoid',
  5778: 'humanoid',
  5781: 'humanoid',
  5786: 'insect',
  5802: 'reptile',
  5816: 'humanoid',
  5919: 'humanoid',
  5933: 'humanoid',
  5938: 'humanoid',
  5939: 'humanoid',
  5943: 'object',
  5946: 'undead',
  5969: 'undead',
  5972: 'undead',
  5977: 'undead',
  5979: 'humanoid',
  6036: 'object',
  6039: 'object',
  6041: 'object',
  6043: 'object',
  6045: 'object',
  6047: 'object',
  6049: 'object',
  6052: 'object',
  6054: 'object',
  6056: 'object',
  6058: 'object',
  6060: 'object',
  6062: 'object',
  6065: 'object',
  6067: 'object',
  6068: 'object',
  6069: 'object',
  6071: 'object',
  6074: 'object',
  6096: 'beast',
  6100: 'beast',
  6120: 'slime',
  6161: 'insect',
  6290: 'insect',
  6302: 'insect',
  6310: 'beast',
  6314: 'beast',
  6322: 'beast',
  6325: 'beast',
  6331: 'object',
  6339: 'object',
  6345: 'object',
  6348: 'object',
  6396: 'dragon',
  6443: 'humanoid',
  6449: 'aquatic',
  6452: 'golem',
  6477: 'aquatic',
  6480: 'humanoid',
  6483: 'reptile',
  6552: 'object',
  6555: 'object',
  6557: 'object',
  6558: 'humanoid',
  6560: 'humanoid',
  6566: 'humanoid',
  6569: 'flying',
  6581: 'humanoid',
  6592: 'insect',
  6593: 'insect',
  6595: 'humanoid',
  6632: 'beast',
  6634: 'beast',
  6636: 'beast',
  6638: 'golem',
  6645: 'object',
  6649: 'object',
  6664: 'beast',
  6665: 'beast',
  6685: 'golem',
  6699: 'flying',
  6704: 'flying',
  6706: 'object',
  6709: 'object',
  6712: 'object',
  6718: 'object',
  6724: 'object',
  6776: 'undead',
  6778: 'elemental',
  6781: 'humanoid',
  6786: 'undead',
  6793: 'dragon',
  6892: 'object',
  6901: 'undead',
  6909: 'insect',
  6910: 'insect',
  6918: 'insect',
  6919: 'object',
  6920: 'object',
  6992: 'object',
  7001: 'humanoid',
  7009: 'undead',
  7047: 'reptile',
  7050: 'object',
  7197: 'reptile',
  7220: 'beast',
  7257: 'undead',
  7260: 'beast',
  7263: 'object',
  7285: 'beast',
  7335: 'humanoid',
  7342: 'humanoid',
  7351: 'humanoid',
  7362: 'humanoid',
  7365: 'dragon',
  7369: 'dragon',
  7391: 'object',
  7392: 'object',
  7393: 'object',
  7394: 'object',
  7480: 'object',
  7516: 'humanoid',
  7522: 'object',
  7531: 'object',
  7534: 'object',
  7535: 'object',
  7536: 'object',
  7537: 'object',
  7539: 'dragon',
  7548: 'object',
  7550: 'object',
  7552: 'object',
  7554: 'object',
  7557: 'dragon',
  7558: 'dragon',
  7559: 'dragon',
  7566: 'dragon',
  7567: 'object',
  7572: 'object',
  7573: 'dragon',
  7580: 'dragon',
  7585: 'object',
  7587: 'dragon',
  7590: 'dragon',
  7593: 'dragon',
  7596: 'dragon',
  7604: 'dragon',
  7609: 'dragon',
  7655: 'humanoid',
  7658: 'dragon',
  7659: 'dragon',
  7660: 'dragon',
  7661: 'dragon',
  7662: 'dragon',
  7684: 'object',
  7745: 'beast',
  7764: 'beast',
  7784: 'beast',
  7786: 'beast',
  7788: 'beast',
  7805: 'object',
  7860: 'object',
  7862: 'aquatic',
  7864: 'dragon',
  7868: 'humanoid',
  7869: 'dragon',
  7870: 'dragon',
  7905: 'flying',
  7939: 'flying',
  7940: 'flying',
  7941: 'flying',
  7959: 'humanoid',
  7989: 'golem',
  7992: 'golem',
  7995: 'golem',
  7998: 'golem',
  8036: 'dragon',
  8055: 'dragon',
  8056: 'dragon',
  8087: 'elemental',
  8096: 'elemental',
  8126: 'flying',
  8128: 'dragon',
  8138: 'object',
  8175: 'flying',
  8239: 'aquatic',
  8334: 'golem',
  8338: 'humanoid',
  8457: 'undead',
  8459: 'undead',
  8479: 'object',
  8539: 'humanoid',
  8542: 'humanoid',
  8543: 'humanoid',
  8544: 'humanoid',
  8545: 'humanoid',
  8546: 'humanoid',
  8549: 'humanoid',
  8600: 'humanoid',
  8601: 'humanoid',
  8602: 'humanoid',
  8603: 'humanoid',
  8650: 'aquatic',
  8653: 'aquatic',
  8656: 'aquatic',
  8694: 'aquatic',
  8695: 'aquatic',
  8696: 'aquatic',
  8697: 'humanoid',
  8741: 'beast',
  8743: 'humanoid',
  8744: 'beast',
  8748: 'beast',
  8749: 'beast',
  8750: 'beast',
  8751: 'golem',
  9218: 'object',
  9377: 'humanoid',
  9381: 'humanoid',
  9385: 'humanoid',
  9388: 'humanoid',
  9391: 'humanoid',
  9395: 'humanoid',
  9398: 'humanoid',
  9401: 'humanoid',
  9404: 'humanoid',
  9407: 'humanoid',
  9411: 'humanoid',
  9414: 'humanoid',
  9635: 'humanoid',
  9638: 'humanoid',
  9692: 'humanoid',
  9698: 'humanoid',
  9704: 'humanoid',
  9710: 'humanoid',
  10050: 'object',
  10071: 'insect',
  10150: 'object',
  10154: 'humanoid',
  10157: 'humanoid',
  10169: 'humanoid',
  10172: 'humanoid',
  10226: 'aquatic',
  10229: 'aquatic',
  10232: 'humanoid',
  10235: 'humanoid',
  10238: 'humanoid',
  10245: 'humanoid',
  10248: 'humanoid',
  10256: 'dragon',
  10259: 'humanoid',
  10262: 'humanoid',
  10273: 'humanoid',
  10275: 'humanoid',
  10276: 'humanoid',
  10277: 'humanoid',
  10283: 'humanoid',
  10286: 'humanoid',
  10306: 'humanoid',
  10309: 'humanoid',
  10354: 'elemental',
  10566: 'aquatic',
  10573: 'dragon',
  10599: 'beast',
  10609: 'humanoid',
  10610: 'humanoid',
  10611: 'humanoid',
  10612: 'humanoid',
  10613: 'humanoid',
  10614: 'humanoid',
  10615: 'humanoid',
  10616: 'humanoid',
  10617: 'humanoid',
  10619: 'humanoid',
  10620: 'humanoid',
  10622: 'humanoid',
  10623: 'humanoid',
  10624: 'humanoid',
  10625: 'humanoid',
  10627: 'insect',
  10629: 'humanoid',
  10630: 'humanoid',
  10631: 'flying',
  10641: 'golem',
  10642: 'slime',
  10643: 'undead',
  10647: 'undead',
  10648: 'undead',
  10649: 'undead',
  10651: 'undead',
  10654: 'undead',
  10655: 'beast',
  10659: 'humanoid',
  10661: 'humanoid',
  10680: 'undead',
  10697: 'object',
  10717: 'aquatic',
  10720: 'aquatic',
  10723: 'aquatic',
  10726: 'aquatic',
  10729: 'aquatic',
  10732: 'aquatic',
  10735: 'aquatic',
  10738: 'aquatic',
  10741: 'aquatic',
  10742: 'aquatic',
  10743: 'aquatic',
  10744: 'aquatic',
  10750: 'undead',
  10759: 'humanoid',
  10768: 'golem',
  10773: 'beast',
  10790: 'undead',
  10792: 'humanoid',
  10793: 'humanoid',
  10794: 'humanoid',
  10796: 'elemental',
  10797: 'beast',
  10798: 'golem',
  10799: 'golem',
  10800: 'beast',
  10851: 'beast',
  10859: 'object',
  10884: 'elemental',
  10891: 'beast',
  10913: 'undead',
  10969: 'beast',
  10984: 'dragon',
  11092: 'undead',
  11102: 'beast',
  11129: 'humanoid',
  11130: 'humanoid',
  11131: 'humanoid',
  11135: 'humanoid',
  11136: 'beast',
  11137: 'beast',
  11139: 'beast',
  11140: 'humanoid',
  11141: 'humanoid',
  11142: 'elemental',
  11143: 'elemental',
  11144: 'elemental',
  11145: 'elemental',
  11146: 'elemental',
  11147: 'humanoid',
  11148: 'humanoid',
  11149: 'humanoid',
  11150: 'humanoid',
  11152: 'humanoid',
  11155: 'humanoid',
  11156: 'humanoid',
  11158: 'humanoid',
  11159: 'humanoid',
  11160: 'humanoid',
  11161: 'humanoid',
  11162: 'humanoid',
  11163: 'humanoid',
  11164: 'humanoid',
  11165: 'reptile',
  11166: 'humanoid',
  11167: 'humanoid',
  11168: 'humanoid',
  11169: 'humanoid',
  11170: 'humanoid',
  11171: 'humanoid',
  11172: 'humanoid',
  11174: 'dragon',
  11175: 'reptile',
  11178: 'humanoid',
  11197: 'undead',
  11203: 'object',
  11204: 'object',
  11214: 'humanoid',
  11215: 'humanoid',
  11219: 'object',
};
